import CompanyTab from '@/components/setting_tsx/CompanyTab';
import RoleAndCategoryTab from '@/components/setting_tsx/RoleAndCategoryTab';
import ServiceAvatarTab from '@/components/setting_tsx/ServiceAvatarTab';
import SubscriptionPlanTab from '@/components/setting_tsx/SubscriptionPlanTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSettings } from '@/hooks/useSettings';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

// Loading spinner component
const TabLoader = () => (
  <div className="flex items-center justify-center p-8">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Tab configuration for better maintainability
const TAB_CONFIG = [
  { id: 'company', label: 'Company', component: CompanyTab },
  { id: 'roles', label: 'Role & Category', component: RoleAndCategoryTab },
  { id: 'service', label: 'Service Avatar', component: ServiceAvatarTab },
  { id: 'subscription', label: 'Subscription Plan', component: SubscriptionPlanTab },
] as const;

const Setting = () => {
  // Get settings data and loading state
  const { settings, isLoading } = useSettings();

  // Track the active tab
  const [activeTab, setActiveTab] = useState<string>(TAB_CONFIG[0].id);

  // Early return for loading state
  if (isLoading || !settings) {
    return (
      <div className="min-h-screen bg-gray-50/30 p-6">
        <TabLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/30">
      <div className="w-full max-w-7xl mx-auto px-2 sm:px-6 lg:px-4 py-6">
        <div className="flex flex-col space-y-8">
          {/* Header */}
          <div className="flex items-center border-b pb-4">
            <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
          </div>

          {/* Tabs Container */}
          <div className="flex flex-col sm:flex-row sm:space-x-8">
            <Tabs
              defaultValue={TAB_CONFIG[0].id}
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full flex flex-col sm:flex-row sm:space-x-8"
            >
              {/* Tab List */}
              <div className="w-full sm:w-40 mb-4 sm:mb-0">
                <div className="relative">
                  <TabsList className="flex sm:flex-col h-auto sm:space-y-1 bg-transparent p-0 pb-3 sm:pb-0 overflow-x-auto sm:overflow-x-visible whitespace-nowrap scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                    {TAB_CONFIG.map(tab => (
                      <TabsTrigger
                        key={tab.id}
                        value={tab.id}
                        className="flex-shrink-0 sm:w-full justify-start px-3 py-1.5 sm:px-4 sm:py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm"
                      >
                        {tab.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>
              </div>

              {/* Tab Content */}
              <div className="flex-1 ">
                <div className="bg-card text-card-foreground shadow-sm rounded-lg p-2">
                  {TAB_CONFIG.map(tab => {
                    const TabComponent = tab.component;
                    return (
                      <TabsContent
                        key={tab.id}
                        value={tab.id}
                        className="mt-0 focus-visible:outline-none focus-visible:ring-0"
                      >
                        {activeTab === tab.id && <TabComponent />}
                      </TabsContent>
                    );
                  })}
                </div>
              </div>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Setting;
